import { axiosInstance } from "@/lib/axiosInstance";
import { AutocompleteOption } from "@/components/Autocomplete/types";
import {
  AsyncSelectFetchParams,
  AsyncSelectFetchResult,
} from "@/components/form/types";

export type Stream = {
  id: string;
  name: string;
  is_active: boolean;
};

export type Degree = {
  id: string;
  stream_id: string;
  name: string;
  degree_type: string;
  duration_years: number;
  is_active: boolean;
};

const fetchStreams = async (): Promise<AutocompleteOption[]> => {
  try {
    const { data } = await axiosInstance.get<Stream[]>("/v1/master/streams");

    return data.map((stream) => ({
      label: stream.name,
      value: stream.id,
    }));
  } catch (error: any) {
    console.error("Failed to fetch streams:", error);
    return [];
  }
};

const fetchDegreesByStreamId = async (
  stream_id: string
): Promise<AutocompleteOption[]> => {
  try {
    const { data } = await axiosInstance.get<Degree[]>(`/v1/master/degrees`, {
      params: { stream_id },
    });

    return data.map((degree) => ({
      label: degree.name,
      value: degree.id,
    }));
  } catch (error: any) {
    console.error("Failed to fetch degrees:", error);
    return [];
  }
};

const fetchStreamOptions = async (
  params: AsyncSelectFetchParams
): Promise<AsyncSelectFetchResult> => {
  const { search, page, pageSize } = params;
  const { data } = await axiosInstance.get<Stream[]>("/v1/master/streams", {
    params: { search, page, limit: pageSize },
  });

  return {
    items: data.map((stream) => ({
      label: stream.name,
      value: stream.id,
    })),
    hasMore: data.length === pageSize,
    total: data.length, // approximation if total is not returned
  };
};

const fetchDegreeOptions = async (
  stream_id: string,
  params: AsyncSelectFetchParams
): Promise<AsyncSelectFetchResult> => {
  const { search, page, pageSize } = params;
  const { data } = await axiosInstance.get<Degree[]>("/v1/master/degrees", {
    params: { stream_id, search, page, limit: pageSize },
  });

  return {
    items: data.map((degree) => ({
      label: degree.name,
      value: degree.id,
    })),
    hasMore: data.length === pageSize,
    total: data.length, // approximation
  };
};

const fetchCollegeDegreeOptions = async (
  department_id: string,
  params: AsyncSelectFetchParams,
  degree_type?: string
): Promise<AsyncSelectFetchResult> => {
  const { search, page, pageSize } = params;
  const { data } = await axiosInstance.get<Degree[]>("/v1/master/college-degrees", {
    params: { department_id, search, page, limit: pageSize },
  });

  const filteredData = degree_type
    ? data.filter(
        (d) => d.degree_type?.toLowerCase() === degree_type.toLowerCase()
      )
    : data;

  return {
    items: filteredData.map((degree) => ({
      label: degree.name,
      value: degree.id,
    })),
    hasMore: data.length === pageSize,
    total: filteredData.length,
  };
};

export const masterService = {
  fetchStreams,
  fetchDegreesByStreamId,
  fetchStreamOptions,
  fetchDegreeOptions,
  fetchCollegeDegreeOptions,
};
